import { useCallback, useMemo, useState } from "react";
import type { PermissionContextDto } from "@Front/Api/LeCoffreApi/SuperAdmin/RolePermissions/RolePermissions";
import { useRolePermissionsRefresh, Matrix } from "./useRolePermissionsRefresh";
import {
	logMatrixResponse,
	logMatrixStateUpdate,
	handleRefreshError,
} from "./useRolePermissionsControllerHelpers";
import { checkPermissionInMatrix } from "./rolePermissionsMatrixCheck";
import type { MatrixPermissionAction, MatrixPermissionCanFn, MatrixPermissionResource } from "@Front/Utils/permissionCanTypes";

export type { Matrix };
export { logMatrixResponse, logMatrixStateUpdate, handleRefreshError };

export type PermissionContextValue = {
	can: MatrixPermissionCanFn;
	loading: boolean;
	error: string | null;
	matrix: Matrix;
	permissionContext: PermissionContextDto | null;
	refresh: () => Promise<void>;
};

export function useRolePermissionsController(): PermissionContextValue {
	const [matrix, setMatrix] = useState<Matrix>(null);
	const [permissionContext, setPermissionContext] = useState<PermissionContextDto | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	const refresh = useRolePermissionsRefresh(setMatrix, setPermissionContext, setLoading, setError);

	const can = usePermissionChecker(matrix, permissionContext);

	return useMemo<PermissionContextValue>(
		() => ({
			can,
			loading,
			error,
			matrix,
			permissionContext,
			refresh,
		}),
		[can, error, loading, matrix, permissionContext, refresh],
	);
}

function usePermissionChecker(
	matrix: Matrix,
	permissionContext: PermissionContextDto | null,
): MatrixPermissionCanFn {
	return useCallback(
		(resource: MatrixPermissionResource, action: MatrixPermissionAction, scopedEntity?: string | null) =>
			checkPermissionInMatrix({ resource, action, scopedEntity, matrix, permissionContext }),
		[matrix, permissionContext],
	);
}
